import type { Messages } from '../messages.js'

/**
 * Czech. `tah` for flips, the word a Czech board game uses for a move.
 *
 * Four plural forms, and `many` is not a bigger `few`: Czech uses it only for fractions, so
 * `1,5 slova` takes it while `5 slov` takes `other`.
 */
export const cs: Messages = {
  tag: 'cs',

  readingDictionary: 'Načítám slovník…',
  noWordList: 'Pro „{language}“ chybí seznam slov. Vytvořte jej:  pnpm dictionary build',
  emptyWordList: 'Seznam slov pro „{language}“ je prázdný.',

  flips: 'tahy',
  score: 'skóre',
  words: 'slova',
  round: 'kolo',
  ticksLeftLabel: 'Zbývající čas v tomto kole',
  typeAWord: 'napište slovo',
  tapPrompt: 'klepnutím vyberte nebo vraťte písmena, pak {action}',

  boardOfTiles: 'Hrací plocha z {n} kamenů',
  faceDown: 'lícem dolů',
  wildCard: 'žolík',
  wildKey: 'libovolné písmeno',
  letterReplaced: '{from} se změnilo na {to}',
  letterSwap: 'VÝMĚNA PÍSMEN!',
  spentTile: 'použitý kámen',
  hiddenWhilePaused: 'skryto během pauzy',
  letterInWord: '{letter}, {position}. písmeno slova',

  completeWord: 'Dokončit slovo',

  completeShort: 'Dokončit',
  reset: 'Smazat',
  pause: 'Pauza',
  resume: 'Pokračovat',
  newGame: 'Nová hra',
  paused: 'Pozastaveno',
  outOfFlips: 'Došly tahy',
  finalResult: '{score} za {words} ve {rounds}',
  playAgain: 'Hrát znovu',
  share: 'Sdílet',
  shareCopied: 'Zkopírováno.',
  shareSelect: 'Zkopírujte toto:',

  lettersSelect: 'písmena vybírají',
  keysWild: 'vezme se, když napíšete písmeno, které na ploše není',
  clearsEvery: 'smaže všechna vybraná {letter}',
  undoLastLetter: 'vrátit poslední písmeno',
  noWordsYet: 'Zatím žádná slova.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'už nalezeno',
  reasonTooShort: 'příliš krátké',
  reasonNotAWord: 'není to slovo',
  reasonAllFound: 'už je máte všechna',
  noSuchLetterUp: 'žádné odkryté {letter}',
  nothingUp: 'nic není odkryto',
  letterHidden: 'jedno písmeno se skrylo',
  shuffled: 'zamícháno',
  shuffledAndBilled: 'zamícháno, započteno {flips} nevyužitých',
  tooFewLetters: 'Zbývá příliš málo písmen',

  gameLanguage: 'jazyk',
  interfaceLanguage: 'rozhraní',
  dictionarySize: '{common} běžných z {full} slov',
  filterLanguages: 'Hledat jazyk',
  noMatches: 'Žádná shoda',

  nerdMode: 'režim pro fajnšmekry',
  rules: 'Pravidla',
  difficulty: 'obtížnost',
  difficultyNames: { easy: 'snadná', medium: 'střední', hard: 'těžká', insane: 'šílená' },
  pausedNoBoard: 'Tato hra se nedostane do žebříčku.',
  about: 'O hře',
  themeLabel: 'téma',
  themeNames: { traditional: 'tradiční', light: 'světlé', contrast: 'vysoký kontrast' },
  tiles: 'kameny (N)',
  secondsPerTick: 'sekundy / takt',
  holdTicks: 'takty výdrže',
  minWord: 'nejkratší slovo',
  startingFlips: 'tahy na začátku',
  wildChance: 'šance na žolíka',
  replaceChance: 'šance na výměnu písmen',
  hideChance: 'šance na skrytí písmene',
  wordCompleteMode: 'po dokončení slova',
  wordCompleteNames: { shuffle: 'zamíchat', spend: 'spotřebovat', keep: 'ponechat' },
  flipEconomy: 'návrat tahů',
  flipEconomyNames: {
    none: 'žádný',
    perLetter: 'za písmeno',
    fibonacci: 'fibonacci',
    overMinimum: 'nad minimum',
  },
  repeatedLetterKey: 'klávesa opakovaného písmene',
  keySchemeNames: { cycle: 'cyklus', advance: 'postup' },
  keySchemeHelp: {
    cycle:
      'A vezme další nepoužité A, a jakmile jsou všechna ve slově, smaže je. ' +
      'Shift+A je smaže také.',
    advance: 'A vezme další nepoužité A. Shift+A smaže ze slova všechna A.',
  },

  whatThatMeans: 'Co to znamená',
  factRound: 'kolo',
  factWholeBoardUp: 'celá plocha odkrytá na',
  factRoundCosts: 'kolo stojí',
  factFlipsBuy: 'počáteční tahy koupí',
  factThisBoard: 'tato plocha',
  factBoardHadToAdmit: 'plocha musela připustit',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, nejdelší {longest}',
  wordsIncludingOneOf: '{words}, z toho jedno z {ceiling}',
  scorelessRounds: '{rounds} bez bodů',

  whatAWordPays: 'Co slovo vynese',
  columnLetters: 'písmena',
  columnCost: 'cena',
  columnPoints: 'body',
  columnFlips: 'tahy',
  columnNet: 'rozdíl',

  canonicalRules: 'Standardní pravidla pro {difficulty}.',
  customRules: 'Změněno oproti přednastavení. Skóre podle vlastních pravidel se nezapočítává.',
  applyAndStart: 'Použít a začít novou hru',
  changesNextGame: 'Změny se projeví v příští hře.',
  presets: 'Přednastavení:',

  start: 'Začít',
  restart: 'Znovu',
  quit: 'Ukončit',
  quitTitle: 'Ukončit tuto hru?',
  restartTitle: 'Začít tuto hru znovu?',
  restartConfirm: 'Znovu',
  quitConfirm: 'Ukončit',
  keepPlaying: 'Hrát dál',
  personalBest: 'Vaše nejlepší hry',
  newPersonalBest: 'Nový osobní rekord.',
  columnRank: '#',
  notRanked: 'Vlastní pravidla, takže tato hra se nezapočítává.',
  notRankedPaused: 'Hodiny se zastavily, takže tato hra se nezapočítává.',
  rankOfTotal: '{rank} z {total}',

  howToPlay: 'Jak hrát',

  backToGame: 'Zpět do hry',
  welcomeTitle: 'Vítejte v Blinkered',
  tutorialSkip: 'Přeskočit',
  tutorialNext: 'Dále',
  tutorialBack: 'Zpět',
  tutorialStart: 'Začít hrát',
  tutorialHideAgain: 'Příště nezobrazovat',
  tutorialProgress: '{n} z {total}',
  tutorialSkipTitle: 'Přeskočit úvod?',
  tutGoalTitle: 'Blinkered je závod',
  tutGoalBody:
    'Hru začínáte s pevným počtem tahů a každý odkrytý kámen stojí jeden tah. Když tahy dojdou, hra končí. Za slova tahy získáváte, a čím delší slovo, tím víc tahů dostanete.',
  tutSpentBody:
    'Písmena, která použijete ve slově, zůstanou zakrytá až do dalšího kola. Která písmena jsou na ploše, změní jen výměna písmene.',
  tutChangeTitle: 'Písmena se mohou měnit',
  tutSameLetters:
    'Všimli jste si slova, které jste nestihli zahrát? Stejná písmena se vrátí v dalším kole, zamíchaná na nová místa.',
  tutPickLetters: 'Klepejte na písmena v pořadí, ve kterém chcete složit slovo.',
  tutMoreTurn: 'Kameny se odkrývají, i když přemýšlíte, takže lepší písmeno může teprve přijít.',
  tutTapBack:
    'Vzali jste písmeno, které jste nechtěli? Klepněte na ně znovu a vrátí se. Kterékoli, nejen poslední.',
  tutComplete: 'Až bude slovo hotové, stiskněte Dokončit.',
  htBoardTitle: 'Plocha',
  htBoardBody: 'Kameny se odkrývají po jednom, v náhodném pořadí. Z odkrytých se skládají slova.',
  htWordsTitle: 'Slova',
  htWordsBody: 'Slovo složíte z odkrytých kamenů psaním nebo klikáním na písmena v pořadí.',
  htFlipsTitle: 'Tahy',
  htFlipsBody:
    'Každý odkrytý kámen stojí tah. Dokončené slovo tahy vrátí, a delší slova vrátí více. Když tahy dojdou, hra končí.',
  htRoundTitle: 'Kolo',
  htRoundBody:
    'Když se odkryje poslední kámen kola, celá plocha na okamžik vydrží. Pak se kameny otočí a zamíchají a začne nové kolo.',
  htLanguagesTitle: 'Jazyky',
  htLanguagesBody:
    'Je jich {n}. Každou plochu lze vyřešit slovy, která lidé opravdu používají. Neobvyklé slovo boduje také, pokud je slovník zná.',
  htKeysTitle: 'Klávesnice',
  htWildTitle: 'Žolíci',
  htWildBody:
    'Občas se místo písmene objeví žolík. Žolík platí za kterékoli písmeno, které tvoří platné slovo. Už dokončené slovo se nepočítá.',
  htHideTitle: 'Skrývající se písmena',
  htHideBody:
    'Na vyšších úrovních se odkryté písmeno může samo zakrýt a vrátit se později v kole. Časomíra za to dostane takt navíc, červeně. Již vybraná písmena se nezakryjí.',
  tutHideBody:
    'Na vyšších úrovních se odkryté písmeno může samo zakrýt a vrátit se později v kole. Již vybraná písmena se nezakryjí.',
  htSwapTitle: 'Měnící se písmena',
  htSwapBody:
    'Občas se mezi koly jedno písmeno vymění za jiné. Uvidíte, které zmizelo a které přibylo.',
  htLevelsTitle: 'Obtížnosti',
  htLevelEasy:
    'Stejných dvanáct písmen po celou hru, takže se dají naučit a nosit seznam slov v hlavě. Kameny se odkrývají pomalu a celá plocha zůstává vidět dost dlouho na dokončení výběru.',
  htLevelMedium:
    'Tu a tam se písmeno změní, takže je těžší pamatovat si slova odložená na později. Méně času na dívání i na přemýšlení.',
  htLevelHard:
    'Třípísmenná slova přestanou platit a písmeno se mění zhruba každé druhé kolo. Plocha se sotva ukáže a už se míchá.',
  htLevelInsane: 'Všechno najednou a naplno. Plocha se zamíchá téměř hned po posledním tahu.',
  htTouchTitle: 'Dotyková obrazovka',
  htTouchBody:
    'Klepnutím na odkrytý kámen vezmete jeho písmeno. Klepnutím na vzaté písmeno je vrátíte. Dokončit a Smazat jsou pod plochou.',

  accountTitle: 'Tvůj účet',
  menuProfile: 'Můj profil',
  menuGames: 'Moje hry',
  menuPublicPage: 'Moje veřejná stránka',
  menuSignedInAs: 'Přihlášen jako',
  signIn: 'Přihlásit se',
  signOut: 'Odhlásit se',
  signInTitle: 'Přihlas se nebo si založ účet',
  signInLead: 'Účet uchová tvoje hry, na každém zařízení.',
  signInKeepGame: 'Přihlas se a tahle hra ti zůstane, i všechny další.',
  continueWithApple: 'Pokračovat s Apple',
  continueWithGoogle: 'Pokračovat s Google',
  signInOr: 'nebo',
  emailLabel: 'E-mail',
  codeLabel: 'Kód',
  emailMeACode: 'Pošlete mi kód',
  working: 'Moment…',
  sendNewCode: 'Poslat nový kód',
  codeSent: 'Podívej se do pošty. Kód platí 10 minut.',
  notNow: 'Teď ne',
  badEmail: 'Tohle nevypadá jako e-mailová adresa.',
  badCode: 'Ten kód nefungoval. Kódy vyprší po 10 minutách a platí jednou.',
  serverBusy: 'Server je nedostupný. Zkus to za chvíli.',
  serverDown: 'Server je nedostupný.',
  offline: 'Offline',
  leaderboardTitle: 'Žebříček',
  leaderboardEmpty: 'V tomto žebříčku ještě nejsou žádné hry. Buď první.',
  leaderboardWouldBe: 'Kde by tato hra stála',
  leaderboardThisGame: 'Tato hra',
  leaderboardRanksHere: 'Kde tato hra stojí',
  leaderboardOutside: 'Mimo první pětku.',
  tutAccountTitle: 'Ulož si své skóre',
  tutAccountBody: 'Účet ukládá tvé hry, zapisuje tvé skóre do žebříčku a spravuje tvůj profil.',
  ssoCancelled: 'Přihlášení zrušeno.',
  ssoExpired: 'Trvalo to moc dlouho. Přihlas se znovu.',
  ssoNoUsername: 'Účet se nepodařilo dokončit. Zkus to za chvíli.',
  ssoFailed: 'Nepovedlo se. Zkus to znovu, nebo použij e-mail.',
  tabProfile: 'Profil',
  countryAny: 'Raději neřeknu',
  findCountry: 'Hledat země',
  usernameLabel: 'Uživatelské jméno',
  bioLabel: 'O mně',
  countryLabel: 'Země',
  bioHint: 'Žádné odkazy. {left}/{max}',
  save: 'Uložit',
  saving: 'Ukládám…',
  saved: 'Uloženo.',
  nameTaken: 'To jméno už někdo má.',
  nameTooShort: 'Trochu delší, prosím.',
  nameTooLong: 'To je moc dlouhé.',
  nameBadCharacters: 'Jen písmena, číslice, pomlčky a podtržítka.',
  nameBadEdges: 'Musí začínat a končit písmenem nebo číslicí.',
  nameMixedScripts: 'Vyber jednu abecedu a zůstaň u ní.',
  nameReserved: 'Tohle vypadá jako jméno, které rozdáváme my. Vyber jiné.',
  nameUnusable: 'Tohle jméno nejde použít.',
  bioTooLong: 'Popisek má nejvýš {max} znaků.',
  bioHasLink: 'Odkazy v popisku nejsou povolené.',
  bioHasControl: 'Obsahuje znaky, které popisek mít nemůže.',
  bioUnusable: 'Tenhle popisek nejde použít.',
  gamesHeading: 'Hry',
  gamesEmpty: 'Zatím tu nic není. Hry, které odehraješ přihlášený, se objeví tady.',
  gamesEmptyShort: 'Zatím tu nic není.',
  gamesLoading: 'Načítám tvoje hry…',
  theirGamesLoading: 'Načítám jejich hry…',
  profileLoading: 'Načítám profil…',
  playerNotFound: 'Nikdo tu takhle nejmenuje.',
  columnWhen: 'Kdy',
  columnRounds: 'kol',
  columnGame: 'Hra',
  gameLoading: 'Načítám hru…',
  gameUnreadable: 'Tuhle hru se nepodařilo načíst.',
  gameNotRanked: 'Hráno s upravenými pravidly, takže se nezapočítává.',
  detailNotKept: 'Slova a hrací plány téhle hry se už neuchovávají.',
  openingBoard: 'Úvodní plán',
  roundByRound: 'Kolo po kole',
  roundNothing: 'nic nenalezeno',
  changeReplaced: '{letter} vyměněno na místě {slot}',
  changeWildOn: '{letter} žolík na místě {slot}',
  changeWildOff: '{letter} žolík pryč z místa {slot}',
  keepThisGame: 'Zachovat tuhle hru',
  savedToYourGames: 'Uloženo mezi tvoje hry.',

  reportThis: 'Nahlásit',
  reportHeading: 'Nahlásit tohle',
  reportTheName: 'Jméno',
  reportTheBio: 'Popis',
  reportTheScore: 'Skóre',
  reportWhy: 'Co je na tom špatně? Nepovinné.',
  reportSend: 'Odeslat hlášení',
  reportSent: 'Děkujeme. Někdo se na to podívá.',
  reportSignIn: 'Přihlas se, abys mohl něco nahlásit.',
  reportGone: 'Tohle už tu není.',
  reportCancel: 'Zrušit',

  deleteAccount: 'Smazat můj účet',
  deleteAccountWhat:
    'Tím se navždy odstraní tvůj profil a každá uložená hra. Nedá se to vzít zpět.',
  deleteAccountAsk: 'Pošlete mi kód',
  deleteConfirm: 'Smazat navždy',
  deleteGone: 'Tvůj účet je smazaný. Díky, že jsi hrál.',
  deleteNoAddress:
    'U tohoto účtu není potvrzená adresa, takže smazání nelze ověřit. Napiš nám a uděláme to ručně.',

  rulesetFacts: 'Slova od {letters} · nový kámen jednou za {seconds}',
  plurals: {
    words: { one: '{n} slovo', few: '{n} slova', many: '{n} slova', other: '{n} slov' },
    rounds: { one: '{n} kolo', few: '{n} kola', many: '{n} kola', other: '{n} kol' },
    flips: { one: '{n} tah', few: '{n} tahy', many: '{n} tahu', other: '{n} tahů' },
    ticks: { one: '{n} takt', few: '{n} takty', many: '{n} taktu', other: '{n} taktů' },
    points: { one: '{n} bod', few: '{n} body', many: '{n} bodu', other: '{n} bodů' },
    minLetters: { one: '{n} písmene', few: '{n} písmen', many: '{n} písmene', other: '{n} písmen' },
    seconds: { one: '{n} sekundy', few: '{n} sekundy', many: '{n} sekundy', other: '{n} sekundy' },
  },
}
